/**
 * The player's hand and wand, bottom-right of frame.
 *
 * Parented to the camera so it never separates from the view, but it lags the
 * look direction slightly — that parallax is most of what sells a first-person
 * arm. Breathing, recoil, and the cast pose all layer on top of the same rig.
 */
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { useCameraSpace } from '../camera/useCameraSpace';
import { scene as bus } from '../scene/sceneBus';
import { glowTexture } from '../assets/textures';

/**
 * Recoil handle. The input layer calls `wandRig.kick()` the instant the server
 * confirms a cast, so the punch lines up with the projectile leaving the tip.
 */
export const wandRig = { kick: (_amount: number) => {} };

/** Resting pose in camera space. */
const REST = new THREE.Vector3(0.34, -0.32, -0.62);
const CAST = new THREE.Vector3(0.2, -0.2, -0.52);

export function PlayerWand() {
  const anchor = useRef<THREE.Group>(null);
  const rig = useRef<THREE.Group>(null);
  useCameraSpace(anchor);

  const wand = useRef<THREE.Group>(null);
  const tipGlow = useRef<THREE.Sprite>(null);
  const tipLight = useRef<THREE.PointLight>(null);

  const glow = useMemo(() => glowTexture(), []);
  const lastYaw = useRef(0);
  const lastPitch = useRef(0);
  const sway = useRef(new THREE.Vector2());
  const recoil = useRef(0);

  // Registered so the input layer can punch the wand the instant a cast lands.
  wandRig.kick = (amount: number) => {
    recoil.current = Math.min(1.2, recoil.current + amount);
  };

  useFrame((state, delta) => {
    const group = rig.current;
    if (!group) return;

    const t = state.clock.elapsedTime;

    // Look-lag parallax.
    const dYaw = bus.yaw - lastYaw.current;
    const dPitch = bus.pitch - lastPitch.current;
    lastYaw.current = bus.yaw;
    lastPitch.current = bus.pitch;
    sway.current.x += (-dYaw * 2.4 - sway.current.x) * Math.min(1, delta * 9);
    sway.current.y += (dPitch * 1.8 - sway.current.y) * Math.min(1, delta * 9);
    sway.current.multiplyScalar(0.92);

    const target = bus.casting ? CAST : REST;
    const breathe = Math.sin(t * 1.35) * 0.008;
    const bob = Math.sin(t * 0.8) * 0.005;

    group.position.x += (target.x + sway.current.x + bob - group.position.x) * Math.min(1, delta * 10);
    group.position.y += (target.y + sway.current.y + breathe - recoil.current * 0.05 - group.position.y) * Math.min(1, delta * 10);
    group.position.z += (target.z + recoil.current * 0.06 - group.position.z) * Math.min(1, delta * 10);

    group.rotation.z += ((bus.casting ? -0.18 : -0.34) + sway.current.x * 1.4 - group.rotation.z) * Math.min(1, delta * 8);
    group.rotation.x += ((bus.casting ? -0.42 : -0.12) - recoil.current * 0.35 - group.rotation.x) * Math.min(1, delta * 8);

    recoil.current = Math.max(0, recoil.current - delta * 4.5);

    // Tip light: brightens with the circle build-up.
    const heat = bus.circle.active ? 0.25 + bus.circle.progress * 0.9 : bus.casting ? 0.3 : 0.12;
    if (tipGlow.current) {
      const mat = tipGlow.current.material as THREE.SpriteMaterial;
      mat.opacity = heat;
      mat.color.setStyle(bus.castColor);
      tipGlow.current.scale.setScalar(0.09 + heat * 0.14);
    }
    if (tipLight.current) {
      tipLight.current.intensity = heat * 3.2;
      tipLight.current.color.setStyle(bus.castColor);
    }
    if (wand.current) {
      wand.current.rotation.z = Math.sin(t * 8) * (bus.casting ? 0.02 : 0.004);
    }
  });

  return (
    <group ref={anchor}>
      <group ref={rig} position={REST.toArray()}>
      <group ref={wand}>
        {/* Forearm sleeve */}
        <mesh position={[0.05, -0.13, 0.13]} rotation={[0.95, -0.1, 0.05]} castShadow>
          <capsuleGeometry args={[0.043, 0.2, 4, 10]} />
          <meshStandardMaterial color="#1e2748" roughness={0.72} metalness={0.08} />
        </mesh>
        {/* Cuff */}
        <mesh position={[0.033, -0.075, 0.075]} rotation={[0.95, -0.1, 0.05]}>
          <cylinderGeometry args={[0.05, 0.052, 0.045, 12]} />
          <meshStandardMaterial color="#9a8143" roughness={0.42} metalness={0.65} />
        </mesh>
        {/* Hand */}
        <mesh position={[0.02, -0.045, 0.035]} rotation={[0.9, -0.1, 0.05]} castShadow>
          <capsuleGeometry args={[0.031, 0.055, 4, 10]} />
          <meshStandardMaterial color="#c79a7c" roughness={0.78} />
        </mesh>
        {/* Wand shaft */}
        <mesh position={[-0.012, 0.055, -0.115]} rotation={[0.86, -0.08, 0.04]} castShadow>
          <cylinderGeometry args={[0.0055, 0.011, 0.36, 8]} />
          <meshStandardMaterial color="#241a12" roughness={0.5} metalness={0.15} />
        </mesh>
        {/* Grip rings */}
        {[0.0, 0.03, 0.06].map((o, i) => (
          <mesh key={i} position={[0.008 - o * 0.06, -0.005 + o, 0.015 - o * 0.9]} rotation={[0.86, -0.08, 0.04]}>
            <torusGeometry args={[0.0125, 0.0022, 4, 10]} />
            <meshStandardMaterial color="#9a8143" metalness={0.7} roughness={0.35} />
          </mesh>
        ))}
        {/* Tip */}
        <sprite ref={tipGlow} position={[-0.028, 0.128, -0.245]} scale={[0.12, 0.12, 0.12]}>
          <spriteMaterial
            map={glow}
            color="#8fd8ff"
            transparent
            opacity={0.2}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            toneMapped={false}
          />
        </sprite>
          <pointLight ref={tipLight} position={[-0.028, 0.128, -0.245]} color="#8fd8ff" intensity={0.4} distance={4} decay={2} />
        </group>
      </group>
    </group>
  );
}

